/**
 * Per-import column-width persistence (DEV-3298). Imports are not a fit for
 * the project pipeline -- their column widths are a per-user view preference,
 * not a property of the version. Keep them in `localStorage`, keyed by the
 * import id, so they survive reloads without entering project JSON.
 */

/** Bump when auto-size or persistence shape changes (invalidates old entries). */
const STORAGE_VERSION = 2;

const storageKey = (importId: string): string =>
  `nodon.import-column-widths.v${STORAGE_VERSION}.${importId}`;

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'number');

/** Returns the persisted widths for an import, or `undefined` when absent. */
export const loadImportColumnWidths = (
  importId: string,
): number[] | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey(importId));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isNumberArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Persist widths for an import. Silently no-ops when storage is unavailable. */
export const saveImportColumnWidths = (
  importId: string,
  widths: readonly number[],
): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(importId), JSON.stringify(widths));
  } catch {
    // Quota / disabled storage -- safe to drop the preference.
  }
};
